import styled from 'styled-components'

interface TabProps {
  $isFirst?: boolean
  $isLast?: boolean
  $isActive?: boolean
}

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #f5f5f5;
`

export const SubHeaderContainer = styled.div`
  background-color: #fff;
  padding: 1.3rem 4.1rem;
  display: flex;
  justify-content: space-between;
  border-radius: .8rem;
  position: relative;
  z-index: 1;
`

export const LeftSide = styled.div`
  display: flex;
  align-items: center;
`

export const RightSide = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: center;
  position: relative;
  z-index: 5;
`

export const SectionName = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  width: fit-content;
  padding-right: 1rem;
  margin-right: 1rem;
  border-right: 1px solid #EBEBEB;
`

export const TabsDiv = styled.div`
  font-family: 'Inter', sans-serif;
  display: flex;
`

export const Tab = styled.div<TabProps>`
  border: 2px solid #ebebeb;
  padding: 10px 24px 10px 16px;
  color: ${props => props.$isActive ? '#0090D3' : '#696969'};
  font-weight: ${props => props.$isActive ? 700 : 400};
  user-select: none;
  display: flex;
  align-items: center;
  font-size: .8rem;
  cursor: pointer;
  background-color: ${props => props.$isActive ? '#E6F4FB' : 'transparent'};
  border-color: ${props => props.$isActive ? '#0090D3' : '#ebebeb'};
  border-radius: ${props => {
    if (props.$isFirst) return '2rem 0 0 2rem';
    if (props.$isLast) return '0 2rem 2rem 0';
    return '0';
  }};

  svg {
    width: 16px;
    height: 16px;
    margin-right: 0.5rem;
  }
`

export const ButtonCreate = styled.div`
  font-weight: bold;
  user-select: none;
  display: flex;
  height: 100%;
  align-items: center;
  font-size: .8rem;
  padding: 0 1.5rem;
  color: #fff;
  cursor: pointer;
  background-color: #0090D3;
  border-radius: 2rem;
  position: relative;
  z-index: 10;
  pointer-events: auto;
`

export const BackButton = styled.div`
  font-weight: bold;
  user-select: none;
  display: flex;
  height: 100%;
  align-items: center;
  font-size: .8rem;
  padding: 0 1.5rem;
  color: #0090D3;
  cursor: pointer;
  background-color: transparent;
  border: 2px solid #0090D3;
  border-radius: 2rem;
  position: relative;
  z-index: 10;
  pointer-events: auto;
  transition: all 0.2s ease;

  &:hover {
    background-color: #E6F4FB;
  }
`

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: transparent;
`

export const Content = styled.div`
  flex: 1;
  padding: 0;
  overflow: auto;
  background-color: transparent;
`

export const BreadcrumbsContainer = styled.nav`
  font-size: 14px;
  padding: 1rem 0;
  background-color: transparent;
  
  ol {
    list-style: none;
    padding: 0;
    padding-inline-start: 0;
    margin: 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }
  
  li {
    display: flex;
    align-items: center;
    color: var(--light-text-color, #666);
    
    &:not(:last-child)::after {
      content: '>';
      margin: 0 8px;
      color: var(--input-border, #999);
    }
    
    a {
      text-decoration: none;
      color: #0090d3;
      white-space: nowrap;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }
    
    &:last-child {
      span {
        color: var(--text-color, #333);
        white-space: nowrap;
        cursor: default;
      }
      
      a {
        color: var(--text-color, #333);
        cursor: default;
        text-decoration: none;
      }
    }
  }
`

export const Table = styled.div`
  width: 100%;
  overflow: hidden;
`

export const TableHeader = styled.div`
  display: flex;
  padding: 0;
  background-color: transparent;
`

export const TableHeaderCell = styled.div<{ width?: string }>`
  padding: 0.875rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #696969;
  width: ${props => props.width || 'auto'};
`

export const TableBody = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border-radius: 8px;
`

export const TableRow = styled.div`
  display: flex;
  border-bottom: 1px solid #E5E5E5;
  padding: 0;
  align-items: center;
  
  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #F9F9F9;
  }
`

export const TableCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  font-size: 0.875rem;
  color: #1a1a1a;
  
  &:nth-child(1) {
    width: 8%;
  }
  
  &:nth-child(2) {
    width: 25%;
  }
  
  &:nth-child(3) {
    width: 22%;
    color: #0090D3;
  }
  
  &:nth-child(4) {
    width: 35%;
    color: #696969;
  }
  
  &:nth-child(5) {
    width: 10%;
    justify-content: flex-end;
  }
`

export const StatusCheckbox = styled.div<{ active?: boolean }>`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background-color: ${props => props.active ? '#0090D3' : '#E5E5E5'};
  display: flex;
  align-items: center;
  justify-content: ${props => props.active ? 'flex-end' : 'flex-start'};
  padding: 2px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &::after {
    content: '';
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`

export const CheckIconWrapper = styled.div<{ active?: boolean }>`
  position: absolute;
  right: 4px;
  display: ${props => props.active ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  color: #0090D3;
`

export const SwitchWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

export const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`

export const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background-color: transparent;
  color: #0090D3;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #E6F4FB;
  }
`

export const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #696969;
  font-size: 0.875rem;
`
